/**
 * The QC section's model: a QC run is an ordinary content job whose id ends with `-qc`
 * (the service has no QC marker, so the id is the only durable tag the app can read back).
 * Its pages walk five steps; which of them apply follows the AI steps the run was asked for.
 */
import type { GenerationRunRead } from '../types/content.types';

import { runScores, runUnderstands, runWrites } from './run-summary';

/** The suffix that marks a job as a QC run (after a UUIDv7, so ids still sort by time). */
export const QC_RUN_SUFFIX = '-qc';

/** The prefix early QC runs used — still recognised so those runs keep listing. */
const LEGACY_QC_RUN_PREFIX = 'qc-';

/** The five pages of a QC run, in order. */
export const QC_STEPS = ['setup', 'run', 'review', 'scores', 'summary'] as const;
export type QcStep = (typeof QC_STEPS)[number];

/** The steps a QC run page can open on (`?step=`); Set up is the separate New page. */
export const QC_RUN_STEPS = ['run', 'review', 'scores', 'summary'] as const;
export type QcRunStep = (typeof QC_RUN_STEPS)[number];

/** Each step's tab label (a check that doesn't score names step 4 by `qcStepLabels`). */
export const QC_STEP_LABELS: Record<QcStep, string> = {
  setup: 'Set up',
  run: 'Run',
  review: 'Review',
  // The key stays `scores` so existing `?step=scores` links keep working.
  scores: 'Products & scores',
  summary: 'Summary',
};

/** Step 4's label on a check that understands (or writes) without scoring. */
export const QC_UNDERSTANDING_RESULTS_LABEL = 'Products & attributes';

/** The tab labels for one run: step 4 says "scores" only when the run scores. */
export function qcStepLabels(run: Pick<GenerationRunRead, 'flows'>): Record<QcStep, string> {
  return runScores(run)
    ? QC_STEP_LABELS
    : { ...QC_STEP_LABELS, scores: QC_UNDERSTANDING_RESULTS_LABEL };
}

/** Whether a job is a QC run (started from the QC section) — the suffix, or the legacy prefix. */
export function isQcRunId(runId: string): boolean {
  return runId.endsWith(QC_RUN_SUFFIX) || runId.startsWith(LEGACY_QC_RUN_PREFIX);
}

/**
 * An RFC 9562 UUIDv7: 48-bit Unix ms timestamp, version 7, variant 10, the rest random. The
 * service orders and pages runs by `run_id`, so QC ids must sort by creation time like its own.
 */
export function uuidV7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // Bytes 0–5: the timestamp, big-endian (division, since bitwise ops are 32-bit).
  let ms = now;
  for (let i = 5; i >= 0; i -= 1) {
    bytes[i] = ms % 256;
    ms = Math.floor(ms / 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** A fresh QC run id: a UUIDv7 plus the QC suffix (39 chars; the service accepts up to 96). */
export function newQcRunId(): string {
  return `${uuidV7()}${QC_RUN_SUFFIX}`;
}

/**
 * Which steps a run includes. Review is on when the run works out product type or details
 * (writing jobs do, through the steps writing needs). Step 4 (`scores`) is on when it scores, or
 * when it understands and has review rows to show (`reviewEmpty`: a re-check that reused them).
 */
export function qcStepsFor(
  run: Pick<GenerationRunRead, 'flows'>,
  reviewEmpty = false,
): Record<QcStep, boolean> {
  const review = runUnderstands(run) || runWrites(run);
  return {
    setup: true,
    run: true,
    review,
    scores: runScores(run) || (review && !reviewEmpty),
    summary: true,
  };
}

/**
 * Whether a finished run's Review step has nothing at all to show: no rows to approve (a re-check
 * that reused earlier jobs' product types stamps none) and no written content for the fallback list.
 */
export function qcReviewSkipped(run: Pick<GenerationRunRead, 'flows'>, reviewEmpty: boolean) {
  return reviewEmpty && !runWrites(run);
}

/**
 * The step a QC run opens on. Until the run ends (`running`, paused included) only Run opens —
 * the results steps are closed, whatever the URL asks. After that, the URL's step when the run has
 * it, else the first results page — skipping Review only when it has nothing to show (no rows to
 * approve and nothing written; written content still shows read-only).
 */
export function defaultQcStep(
  run: Pick<GenerationRunRead, 'flows'>,
  running: boolean,
  requested: QcRunStep | undefined,
  reviewEmpty = false,
): QcRunStep {
  const steps = qcStepsFor(run, reviewEmpty);
  if (running) return 'run';
  if (requested && steps[requested]) return requested;
  if (steps.review && !qcReviewSkipped(run, reviewEmpty)) return 'review';
  if (steps.scores) return 'scores';
  return 'summary';
}
